import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Segment,
  Button,
  Header,
  Loader,
  List,
  Input,
  Grid,
  Message,
  Label,
} from 'semantic-ui-react';
import { useIntl } from 'react-intl';
import config from '../../config';
import { store } from '../../store';
import additionalMessages from '../../messages';
import { getWithAuth } from '../../api';
import { uiSet } from '../mainframe/mainframe.reducer';
import Footer from '../../components/Footer';
import ConditionalPopup from '../../components/ConditionalPopup';
import NewProjectDialog from './NewProjectDialog';
import { ProjectListItems, PROJECTS_QUERY_KEY } from './ProjectListItems';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const sortProjects = (p1, p2) => {
  const d1 = Date.parse(p1.last_accessed);
  const d2 = Date.parse(p2.last_accessed);
  return d2 - d1;
};

function ProjectsPanel() {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  // Redux state
  const status = useSelector((state) => state.sockets.status);
  const action = useSelector((state) => state.sockets.action);
  const userLimits = useSelector((state) => state.mainFrame.userLimits);

  // Component state
  const [busy, setBusy] = useState(false);
  const [getProjectName, setGetProjectName] = useState(false);
  const [filter, setFilter] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [error, setError] = useState(null);

  const {
    data: projects = [],
    isFetching,
    error: requestError,
  } = useQuery({
    queryKey: PROJECTS_QUERY_KEY,
    queryFn: async () => {
      return await getWithAuth(`projects`);
    },
    initialData: null,
    retry: false,
    enabled: true,
  });
  const qc = useQueryClient();

  useEffect(() => {
    if (status === 'success' && action === 'import') {
      qc.invalidateQueries({ queryKey: PROJECTS_QUERY_KEY });
    }
  }, [status, action, dispatch]);

  if (isFetching || !projects) {
    return <Loader active inline />;
  }

  // sort the projects before displaying
  projects.sort(sortProjects);

  const createProject = () => {
    setGetProjectName(true);
  };

  const cancelCreateProject = () => {
    setGetProjectName(false);
  };

  const handleImportProject = () => {
    dispatch(uiSet({ showLPImportDialog: true }));
  };

  const onClearFilter = () => {
    setFilter('');
    setShowAll(false);
  };

  const onFilterChange = (e, { value }) => {
    setFilter(value);
  };

  const onShowAll = () => {
    setShowAll(true);
  };

  // Render logic

  const canCreateProjects = projects.length < userLimits?.projects;
  let filteredProjects = projects;
  try {
    const exp = new RegExp(filter, 'i');
    filteredProjects = projects.filter((p) => exp.test(p.project.name));
  } catch {}

  const displayShowMore = !showAll && filteredProjects.length > 10;
  if (!showAll) {
    filteredProjects = filteredProjects.slice(0, 10);
  }

  return (
    <>
      <Grid container columns={2}>
        <Grid.Column
          widescreen={8}
          largeScreen={8}
          mobile={16}
          tablet={16}
          computer={16}
        >
          <Segment basic size="large">
            <NewProjectDialog
              isOpen={getProjectName}
              onClose={cancelCreateProject}
            />
            <ConditionalPopup
              showPopup={!canCreateProjects}
              message={formatMessage(additionalMessages.maximumNumberError, {
                entityName: 'project',
                limit: store.getState().mainFrame.userLimits?.projects,
              })}
            >
              <Button
                content={formatMessage(additionalMessages.createNewProject)}
                onClick={createProject}
                disabled={busy || !canCreateProjects}
                data-testid="createNewProject"
              />
            </ConditionalPopup>
            <ConditionalPopup
              showPopup={!canCreateProjects}
              message={formatMessage(additionalMessages.maximumNumberError, {
                entityName: 'project',
                limit: store.getState().mainFrame.userLimits?.projects,
              })}
            >
              <Button
                content={formatMessage(
                  additionalMessages.importLINKPlannerProject
                )}
                onClick={handleImportProject}
                disabled={busy || !canCreateProjects}
                data-testid="importLINKPlannerProject"
              />
            </ConditionalPopup>
            {error && <Message error>{error}</Message>}
            {filteredProjects.length > 0 || filter !== '' ? (
              <>
                <Header data-testid="recentProjects">
                  Projects
                  <Label
                    className="float-right"
                    title="LINKPlanner projects"
                    color="black"
                  >
                    {projects.length}
                  </Label>
                </Header>
                <Input
                  placeholder={formatMessage(additionalMessages.filter)}
                  fluid
                  icon={{
                    name: 'cancel',
                    onClick: onClearFilter,
                    link: true,
                    title: formatMessage(additionalMessages.clearFilter),
                  }}
                  value={filter}
                  onChange={onFilterChange}
                  autoFocus
                  data-testid="filter"
                />

                <List
                  celled
                  style={{
                    maxHeight: '72vh',
                    overflowY: 'auto',
                    minHeight: '40vh',
                  }}
                >
                  <ProjectListItems
                    projectsToDisplay={filteredProjects}
                    setError={setError}
                  />
                </List>

                {displayShowMore ? (
                  <Button onClick={onShowAll} data-testid="showAll">
                    {formatMessage(additionalMessages.showAll)}
                  </Button>
                ) : null}
              </>
            ) : (
              <Segment basic>
                <Header data-testid="gettingStartedWithLinkPlanner">
                  Getting started with LINKPlanner
                </Header>
                <p>
                  Click on <strong>"Create new project"</strong> button to start
                  a new LINKPlanner project.
                </p>
                <p>
                  To return to this menu and see your project list at any time,
                  click on the <strong>"Projects" -> "Project List"</strong> in
                  the header menu.
                </p>
                <p>
                  For more information, see the&nbsp;
                  <a
                    href={config.userGuide}
                    title={formatMessage(additionalMessages.userGuide)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="userGuide"
                  >
                    user guide
                  </a>
                  .
                </p>
              </Segment>
            )}
            <Loader active={busy} />
          </Segment>
        </Grid.Column>
        {window?.runtime?.marketingImage && (
          <Grid.Column textAlign="center" only="large screen">
            <a
              href={window?.runtime?.marketingUrl}
              target="_blank"
              rel="noreferrer"
              title={window?.runtime?.marketingTitle}
            >
              <img
                alt={window?.runtime?.marketingTitle}
                style={{ margin: '20px' }}
                border="0"
                src={window?.runtime?.marketingImage}
                data-testid="marketingImage"
              />
            </a>
          </Grid.Column>
        )}
      </Grid>
      <Footer about></Footer>
    </>
  );
}

export default ProjectsPanel;
